import moment, { Moment } from 'moment';
import {history, useIntl} from "umi";
import {useMemo} from "react";
import { isEmpty, isNaN } from "lodash-es";
import { MomentFormatMap } from '@dbl/core/date';
import { STATISTICS_LIST, STATISTICS_PREFIX } from '@dbl/core/global';

interface IUrlInfo {
  system: string; // 系统
  urlId: string | undefined; // gamecode
  realPath: string; // 当前真实路径
}

/**
 * 数字增加千分位
 */
export function formatNumberByComma(number) {
  if (isNaN(parseFloat(number))) {
    return number;
  }
  function formatNumber(num: number|string) {
    // eslint-disable-next-line no-param-reassign
    num = num.toString();
    const reg = num.indexOf('.') > -1 ? /(\d)(?=(\d{3})+\.)/g : /(\d)(?=(\d{3})+$)/g;
    return num.replace(reg, '$1,');
  }
  // 整数部分返回千分隔符格式
  return formatNumber(number);
  // 如果是小数, 最多保留四位
  // return /^(-?\d+)(\.\d+)?$/.test(number) ? result.replace(/^(.*\..{4}).*$/,"$1") : result;
}

export const checkNullObj = (object) => {
  if (object === null || object === undefined) return true;
  if (JSON.stringify(object) === '{}') return true;
  return false;
};

// 判断空字符
export const checkNullString = (value) => value === '' || value === null || value === undefined;

// 数值是否极值
export const getIsErrorValue = (value) => {
  if (parseFloat(value) === 0) return false;
  return !Boolean(value) || !Boolean(parseFloat(value));
};

/**
 * 数值单位转换成 B/M/K (一般用于echarts Y轴)
 * @param {number} value 原始数值
 * @returns 转换后的字符串
 */

export const unitConversion = (data, isFloat = false) => {
  if (getIsErrorValue(data)) return '-';
  if (!isNaN(data) && data >= 1000) {
    // eslint-disable-next-line no-param-reassign
    data = Math.round(data / 10) * 10;
  }
  let unit = 'K';
  let value = Math.abs(data);
  if (!Boolean(data) && parseFloat(data) !== 0) return '-';
  const isNegativeStr = data < 0 ? '-' : '';
  // 最外面的 parseFloat用于去除末尾无效的零
  if (value < 1000000 && value >= 1000) {
    unit = 'K';
    value = parseFloat((parseFloat(value) / 1000).toFixed(2));
  } else if (value >= 1000000 && value < 1000000000) {
    unit = 'M';
    value = parseFloat((parseFloat(value) / 1000000).toFixed(2));
  } else if (value >= 1000000000) {
    unit = 'B';
    value = parseFloat((parseFloat(value) / 1000000000).toFixed(2));
  } else if (value < 1000) {
    unit = '';
    value = isFloat ? data : parseInt(data, 10);
  }
  // return isNegativeStr + Math.abs(value) + unit;

  // Math.abs会去掉末尾的0，为保证数据格式统一，所以不用
  if (value < 0) value = -value;
  return isNegativeStr + value + unit;
};

/**
 * @description:
 默认保留两位小数，涨跌和占比保留一位小数
 如果小数位数不足保留的位数, 则需要补0
 涨跌为 0.0 展示 0,
 如果是空字符串，则显示为横杠 -
 百分比如果超过999.9， 则显示为999.9%+
 * @param {Number|String} num
 * @param {Number} fixedNumber 保留多少位小数，只保留0到4位小数
 * @param {Boolean} isFloating 是否为涨浮
 * @return {String}
 */
export const formatPercentNum = (num, fixedNumber = 2, isFloating = false, isBigPercent = false) => {
  if (getIsErrorValue(num)) return '-';
  if (isNaN(num)) return num;
  // 只保留0到4位小数
  const regPos = /^\d+$/;
  // eslint-disable-next-line no-param-reassign
  if (fixedNumber < 0 || fixedNumber > 4 || !regPos.test(fixedNumber)) fixedNumber = 2;

  // 若为涨跌，则保留一位
  // eslint-disable-next-line no-param-reassign
  if (isFloating) fixedNumber = 1;
  // 取绝对值，因为会传进来负数
  const number = Math.abs(num);
  // 先放大 100 * 10 * fixedNumber 倍，再除 10 * fixedNumber，就能获取到四舍五入后的百分值
  // eslint-disable-next-line no-restricted-properties
  const fixedPow = Math.pow(10, 2 + fixedNumber);
  // eslint-disable-next-line no-restricted-properties
  const floatPow = Math.pow(10, fixedNumber);
  const percentage = (Math.round(number * fixedPow) / floatPow).toFixed(fixedNumber); // toFixed为了补足小数位数

  // 涨跌为0.0展示0
  if (isFloating && Number(percentage) === 0) return '0%';

  if (isBigPercent) {
    return percentage > 9999.99 ? '9999.99%+' : `${percentage}%`;
  }
  return percentage > 999.9 ? '999.9%+' : `${percentage}%`;
};

/**
 * TODO: 重构 这个函数和customDownload.ts中的exportExcelOfDatalab函数重复了，需要重构时重新考虑设计方案.
 * 下载文件
 * @param response 接口返回值
 * @param name 文件名
 */
export const downloadFile = (response, name) => {
  const blob = new Blob([response], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const linkNode = document.createElement('a');
  linkNode.download = name; // a标签的download属性规定下载文件的名称
  linkNode.style.display = 'none';
  linkNode.href = URL.createObjectURL(blob); // 生成一个Blob URL
  document.body.appendChild(linkNode);
  linkNode.click(); // 模拟在按钮上的一次鼠标单击
  URL.revokeObjectURL(linkNode.href); // 释放URL 对象
  document.body.removeChild(linkNode);
};

// 正则表达式判断是否是数字(包含正负小数)
export const isNumber = (value) => {
  if (value) {
    return /^[+-]?\d*\.{0,1}\d{0,1}$/.test(String(value));
  }
  return false;
};

// 深拷贝对象
export function deepClone(data: any, hash = new WeakMap()) {
  if (typeof data !== 'object' || data === null) {
    throw new TypeError('传入参数不是对象');
  }
  // 判断传入的待拷贝对象的引用是否存在于hash中
  if (hash.has(data)) {
    return hash.get(data);
  }
  const newData: any = {};
  const dataKeys = Object.keys(data);
  dataKeys.forEach((value) => {
    const currentDataValue = data[value];
    // 基本数据类型的值和函数直接赋值拷贝
    if (typeof currentDataValue !== 'object' || currentDataValue === null) {
      newData[value] = currentDataValue;
    } else if (Array.isArray(currentDataValue)) {
      // 实现数组的深拷贝；元素可能为 null/基本类型，仅对对象递归
      const array: any[] = [];
      for (const item of currentDataValue) {
        if (typeof item !== 'object' || item === null) {
          array.push(item);
        } else {
          array.push(deepClone(item, hash));
        }
      }
      newData[value] = array;
    } else if (currentDataValue instanceof Set) {
      // 实现set数据的深拷贝
      newData[value] = new Set([...currentDataValue]);
    } else if (currentDataValue instanceof Map) {
      // 实现map数据的深拷贝
      newData[value] = new Map([...currentDataValue]);
    } else {
      // 将这个待拷贝对象的引用存于hash中
      hash.set(data, data);
      // 普通对象则递归赋值
      newData[value] = deepClone(currentDataValue, hash);
    }
  });
  return newData;
}

function fallbackCopyTextToClipboard(text: string) {
  return new Promise((resolve, reject) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      const msg = successful ? 'successful' : 'unsuccessful';
      console.log(`Fallback: Copying text command was ${msg}`);
      resolve('');
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
      reject(err);
    }

    document.body.removeChild(textArea);
  });

}

export function copyTextToClipboard(text) {
  if (!navigator.clipboard) {
    return fallbackCopyTextToClipboard(text);
  }
  return navigator.clipboard.writeText(text);
}

// TODO: 重构时不可以使用useDblIntl函数，而是使用useIntl函数
// 用于获取中英文本
export const useDblIntl = (prefix?:string) => {
  const { formatMessage, locale } = useIntl();
  return useMemo(() => {
    const createDblFormatMessage = (prefix?: string) => {
      const prefixId = prefix ? `dbl_${prefix}` : 'dbl';
      return (id:string, options?: any) => formatMessage({
        id: `${prefixId}_${id}`
      }, options);
    };

    return {
      dblFormatMessage: createDblFormatMessage(prefix),
      commonFormatMessage: createDblFormatMessage("common"),
      createDblFormatMessage,
      formatMessage,
      locale,
      isEn: locale === "en-US"
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

};

// 活跃, 付费, 新进, 留存 中英转换
const specialEnWord: any = {
  "活跃": "Active",
  "付费": "Revenue",
  "新进": "New",
  "留存": "Retention",
  "业务可见": "Visible to Game Team",
  "仅自己可见": "Visible to Me Only",
  "中间表": "DWM",
  "结果表": "APP",
  "模板": "Template"
};


export function translateSpecialWord(cnName: string, isEn: boolean) {
  return isEn ? (specialEnWord[cnName] || cnName) : cnName;
}

// Datalab路径解析
export function parseUrlInfo(str = ''): IUrlInfo {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [_null, system, urlId, ...arr] = str.trim().split('/');
  const realPath = arr.length > 0 ? arr.join('/') : '';
  return {
    system,
    urlId,
    realPath
  };
}

export function getDynamicDateText({lastCount, dateType, from, formatMessage}): string {
  let label = '';
  // eslint-disable-next-line eqeqeq
  if (lastCount == 0) { // 今年
    const suffix = formatMessage({id: `dbl_date_from`}, {from: formatMessage({id: `dbl_date_${dateType}_ago_current`})}).toLowerCase();
    label = `${formatMessage({id: `dbl_date_year_prefix`})}${suffix}`;
    // eslint-disable-next-line eqeqeq
  } else if (lastCount == -1) { // 今天, 本周, 本月
    const suffix = formatMessage({id: `dbl_date_from`}, {from: formatMessage({id: `dbl_date_${dateType}_ago_current`})}).toLowerCase();
    label = `${formatMessage({id: `dbl_date_${dateType}_last_prefix_current`})}${suffix}`;
  } else {
    let prefix = '';
    if (lastCount === '1') {
      prefix = formatMessage({id: `dbl_date_${dateType}_last_prefix_1`});
    } else if (lastCount === 1) {
      prefix = dateType === 'day' ? formatMessage({id: `dbl_date_${dateType}_last_1`}) : formatMessage({id: `dbl_date_${dateType}_last_prefix_1`});
    } else if (lastCount == -2) { // 今天至今
      prefix = formatMessage({id: `dbl_date_hour_now`});
    }else {
      prefix = formatMessage({id: `dbl_date_${dateType}_last_prefix`}, {last: lastCount});
    }

    // 计算文本 后缀
    const fromValue = from || '1';
    let fromText = '';
    if (fromValue === '-1') {  // 今天, 本周, 本月
      fromText = formatMessage({id: `dbl_date_${dateType}_ago_current`});
    } else if (fromValue === '1') { // 昨天, 上周, 上月
      fromText = formatMessage({id: `dbl_date_${dateType}_ago_1`});
    } else {
      fromText = formatMessage({id: `dbl_date_${dateType}_ago`}, {ago: fromValue});
    }
    const suffix = formatMessage({id: `dbl_date_from`}, {from: fromText}).toLowerCase();

    label = lastCount == -2 ? prefix : `${prefix}${suffix}`;
  }
  return label;
}

export function getFilterDateValue(dates: [Moment, Moment], dateType: string) {
  if (dateType === 'hour') {
    return dates?.map(date => date?.format(MomentFormatMap.hour)) || [];
  }
  
  return [
    dates[0]?.startOf(dateType).format('YYYY-MM-DD'),
    dates[1]?.endOf(dateType).format('YYYY-MM-DD')
  ];
}

export function getStaticDateText(dates:[string, string]=['', ''], dateType: string) {

  return dates?.map(date => moment(date)?.format(MomentFormatMap[dateType] || 'YYYY-MM-DD'))?.join(' ~ ') || '';
}

export function getLastDateRange(lastCount: number | string, dateType: string, from?: string, dateFormat?: string): [string, string] {
  const format = dateFormat || (dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD');

  // eslint-disable-next-line
  if (lastCount == 0) { // 今年 (用户不可能自定义输入0)
    return [
      moment().startOf('year').startOf(dateType).format(format),
      moment().format(format)
    ];
  }
  // eslint-disable-next-line eqeqeq
  if (lastCount == -1) {  // 今天, 本周, 本月 (用户不可能自定义输入-1)
    // if (dateType === 'hour') {
    //   return [
    //     moment().subtract(0, dateType).startOf(dateType).format(format),
    //     moment().endOf(dateType).format(format)
    //   ];
    // }
    return [
      moment().subtract(0, dateType).startOf(dateType).format(format),
      moment().endOf(dateType).format(format)
    ];
  }
  
  if (lastCount == -2) { // 目前只有Hour类型有 -2
    // Hour类型 -2 代表 Today now
    return [
      moment().startOf('day').format(format),
      moment().format(format)
    ];
  }
  // 比如 当天是2025-08-06, 上一周应该是 2025-07-28 2025-08-03
  // 但默认进入页面时, 调用方法得到的计算结果是 2025-07-29 2025-08-04
  // 重新点选最近1周选项后, 方法得到的计算结果又是正确的2025-07-28 2025-08-03
  // 前后两次计算输入的参数是一模一样的, 但结果不同
  // 所以week时, startOf和endOf方法需要传入isoWeek, 否则默认进入页面时, Moment计算的开始时间是周二的日期, 结束日期是下周的周一日期
  const unitTime = dateType === 'week' ? 'isoWeek' : dateType;
  
  let fromValue = Number(from || '1');
  fromValue = fromValue > 0 ? fromValue : 0;
  const start = (Number(lastCount) || 7) + fromValue - 1;
  const startDate = moment().subtract(start, dateType).startOf(unitTime).format(format);
  // 结束日期是今天, 本周, 本月时, 截止日期都是 当天, 不是当周的周日.
  // 结束时间是Hour类型的Current now, 截止到 当前小时的 59分59秒.
  const endDate = fromValue ? moment().subtract(fromValue, dateType).endOf(unitTime).format(format) : ((dateType === 'hour') ? moment().endOf(unitTime).format(format) : moment().format(format));
  
  return [
    startDate,
    endDate
  ];
  
}

// 获取数字趋势图卡片所需要的日期粒度和范围
export const getTrendDateInfo = ({value=[], dateType=''}) => {
  let isSingle = true;
  if (value?.length === 1) {
    const dateStr = value[0].startsWith('-') ? value[0].replace('-', '') : value[0];
    // eslint-disable-next-line eqeqeq
    isSingle = Math.abs(dateStr.split('-')[0]) == 1;
  } else {
    const [start, end] = value;
    switch (dateType) {
      case 'week': {
        // 完整的一周 对比 周一日期的endOf等于周日日期(end), 说明是单周, 否则是多周
        isSingle = moment(start).endOf('week').format('YYYY-MM-DD') === end;
        // 特殊情况: 当周; 今天是周一, 那么 end 也是周一日期,
        if (!isSingle) {
          const duration = moment.duration(moment(end).diff(moment(start)));
          isSingle = duration.asDays() <= 6;
        }
        break;
      }
      case 'month': {
        isSingle = moment(start).format('YYYY-MM') === moment(end).format('YYYY-MM');
        break;
      }
      default: {
        isSingle = start === end;
      }
    }
  }
  return {
    granularity: dateType || 'day',
    isSingle,
  };
  
};

// 获取图表日期显示文本
export const getDateText = ({dateRange, dateType='date', dateFormat='YYYY-MM-DD', lastCount=7, from='1'}: {dateRange: string[]; dateType?: string; dateFormat?: string; lastCount?: number; from?: string;}) => {
  if (dateRange?.length === 2) { // 说明是 [2024-04-12, 2024-04-18] 这种格式
    const start = moment(dateRange[0]).startOf(dateType).format(dateFormat);
    const end = moment(dateRange[1]).endOf(dateType).format(dateFormat);
    return [start, end];
  }

  // 否则是 动态日期 [7-days] 这种格式
  return getLastDateRange(lastCount, dateType, from, dateFormat);
  
};

// TODO 日期相关的函数和常量结束 ------------------

/**
 * 需求上要求 中英文互相兜底
 * @param isEn 中英文状态
 * @param cn 中文
 * @param en 英文
 * @param temp 中英文都不存在的时候展示的 (比如数据表名称, 首先展示数据表显示名称的中英文, 如果没有显示名称则展示数据表原始名称)
 */
export const getLocalText = (isEn: boolean, cn: string|undefined, en: string|undefined, temp?: string): string => {
  // eslint-disable-next-line no-param-reassign
  cn = cn || en;
  // eslint-disable-next-line no-param-reassign
  en = en || cn;
  return (isEn ? en : cn) || temp || '';
};

// 翻译后端返回的表格数据中对比项文本的中英翻译
export const getTableCompareText = (isEn: boolean, text: string, isCompareDate = false, isUserFilter = false) => {
  if (isCompareDate) return isEn ? 'Compare Time' : '对比时间';
  if (isUserFilter) return isEn ? 'Comparison' : '对照组';
  const temp: Record<string, string> = {
    '对比项': 'Compare',
    '对比项一': 'Compare1',
    '对比项二': 'Compare2'
  };
  if (!temp[text]) return text;
  
  return isEn ? temp[text] : text;
};

// 更新路径参数，用于保存页面状态
export const updateUrlParams = (newParams: Record<string, string | number | object |undefined> = {}) => {
  const query = history.location.query ?? {};
  Object.entries(newParams).forEach(([key, value]) => {
    if (value !== undefined) {
      query[key] = (typeof value !== 'string') ? JSON.stringify(value) : value;
    } else {
      delete query[key];
    }
  });
  history.replace({pathname: history.location.pathname, query});
};


/**
 * 获取过滤条件的空值文本表示
 * @param value 过滤条件的值
 * @returns 其他文本表示
 */
export const getNullTextOfFilter = (value: string | number | null | undefined): string => {
  if (value === null) {
    return '[NULL]';
  }

  if (value === '') {
    return '[empty string]';
  }
  return String(value);
};

/**
 * 维度选项请求参数：空 keyword 会导致后端 REGEXP_CONTAINS 拼出非法 SQL，需省略该字段
 */
export const sanitizeReportDimensionParams = <T extends { keyword?: string }>(
  params: T,
): Omit<T, 'keyword'> | T => {
  if (params == null) {
    return params;
  }
  const trimmed = params.keyword?.trim();
  if (!trimmed) {
    const { keyword: _keyword, ...rest } = params;
    return rest;
  }
  if (trimmed === params.keyword) {
    return params;
  }
  return { ...params, keyword: trimmed };
};

/**
 * 检查对象是否有空属性值
 * @param obj 要检查的对象
 * @param keys 指定要检查的属性名数组
 * @returns 如果obj不是对象类型或者有属性值为空，则返回true；否则返回false
 */
export const hasEmptyProperty = (obj: any, keys?: string[]): boolean => {
  // 检查obj是否为对象类型且不为null
  if (typeof obj !== 'object' || obj === null) {
    return true; // 如果obj不是对象类型或者为null，直接返回false
  }

  // 获取对象自身的所有属性名
  const newKeys = keys || Object.keys(obj);

  // 遍历属性名数组
  for (const key of newKeys) {
    // 检查属性值是否为null或undefined
    if (obj[key] === null || obj[key] === undefined || isEmpty(obj[key])) {
      return true; // 如果发现有空值，立即返回true
    }
  }
  return false; // 如果所有属性值都不为空，返回false
};

/**
 * 校验输入是否为有效 协议，如果是则直接返回，
 * 如果输入缺少协议（如 `example.com`），会默认添加 `https://` 前缀。
 *
 * @param text - 需要校验的字符串。
 * @returns 返回有效 URL，
 */
export const ensureValidUrl = (text: string): string => {
  // 如果输入不是有效 协议，尝试将其转换为有效 协议
  if (!text?.startsWith?.('http://') && !text?.startsWith?.('https://')) {
    return `https://${text}`;
  }
  return text;
};




/**
 * 快速获取小数位数（不验证输入格式）
 * @param numStr 输入的数字字符串
 * @returns 小数位数
 */
export const getDecimalPlacesFast = (numStr: string): number => {
  // 确保是字符串，并去除首尾空格
  const str = String(numStr).trim();
  // 检查是否是合法数字格式
  if (!/^-?\d*\.?\d+$/.test(str)) {
    return 0; //
  }
  const dotIndex = str.indexOf('.');
  return dotIndex === -1 ? 0 : str.slice(dotIndex + 1).length;
};

/**
 * 转换后统计数据的关键字，用于翻译添加浮窗等操作
 *
 * [legacy · 重构可删] 仅服务 extra_table_data → table.statisticsData → appendUnsortedData 旧链路；
 * 现行统计行由 table_data 单元格 is_statistics / statistics_name 直接渲染。
 *
 * @param statisticsData 统计数据
 * @returns 转换后的统计数据
 */
export const formatStatisticsData = (statisticsData: any[] = []) => {
  // 使用Set保证关键词唯一性
  const statisticsKeywords = new Set(STATISTICS_LIST);

  return statisticsData?.map(item => {
    // 空值检查
    if (!item) return item;

    return Object.fromEntries(
      Object.entries(item).map(([key, value]) => {
        // 检查值是否是统计关键词
        if (statisticsKeywords.has(value as string)) {
          return [key, { value: `${STATISTICS_PREFIX}${value}` }];
        }
        // 处理空值
        if (isEmpty(value)) {
          return [key, { value: '-' }];
        }
        // 普通值
        return [key, { value }];
      })
    );
  }) || [];
};

/**
 * TODO: 重构时考虑 有一个第三方库可以实现这个功能, 可以考虑使用第三方库.
 * 将颜色信息转换为 rgba 格式
 * @param colorInfo 颜色信息对象，包含 hexa 和 alpha 属性
 * @returns rgba 格式的颜色字符串
 */
export const handleColorToRGBA = (colorInfo: { hexa: string; alpha: string; }) => {
  // 解构赋值，获取颜色信息中的 hexa 和 alpha
  const { hexa, alpha } = colorInfo || {};
  const hex = hexa.substring(0, 7); // 去掉最后的 alpha
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  const a = parseInt(alpha) / 100; // alpha 转为 0-1 范围
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/**
 * 将任意类型的值转换为字符串
 * @param value 需要转换的值，可以是任意类型
 * @returns 转换后的字符串形式
 */
export const convertToString = (value: any): string => {
  // 如果没找到值返回空字符串
  if (value === undefined) {
    return "";
  }
  // 如果值已经是字符串或null，直接返回
  if (typeof value === 'string' || value === null) {
    return value;
  }
  // 如果值是对象，使用JSON.stringify转换为字符串
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  // 对于其他类型，使用模板字符串转换为字符串
  return `${value}`;
};